import React, { useState, useEffect, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SA_KEY = process.env.REACT_APP_SUPERADMIN_KEY;

// ── Helpers ──────────────────────────────────────────────────────────────────
const saFetch = (url, options = {}) => {
  const token = localStorage.getItem('sa_token');
  return fetch(`${BASE}/api/superadmin${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Superadmin-Key': SA_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
};

const fc = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fd = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysLeft = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  return diff;
};

// ── Plan badge ────────────────────────────────────────────────────────────────
const PlanBadge = ({ plan }) => {
  const colors = { free: '#6b7280', starter: '#2563eb', pro: '#7c3aed' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 20,
      background: colors[plan] + '18', color: colors[plan],
      border: `1px solid ${colors[plan]}30`
    }}>{plan}</span>
  );
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status, is_active }) => {
  if (!is_active) return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}>SUSPENDED</span>;
  const colors = { active: '#16a34a', trial: '#d97706', expired: '#dc2626', suspended: '#dc2626' };
  const bg = { active: '#dcfce7', trial: '#fef3c7', expired: '#fee2e2', suspended: '#fee2e2' };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg[status] || '#f3f4f6', color: colors[status] || '#6b7280', border: `1px solid ${colors[status] || '#6b7280'}30`, textTransform: 'uppercase' }}>{status}</span>;
};

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  const bg = toast.type === 'success' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#dc2626,#b91c1c)';
  return (
    <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 22px', borderRadius: 12, background: bg, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', minWidth: 280 }}>
      <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: 0, flex: 1 }}>{toast.msg}</p>
      <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', fontSize: 12 }}>✕</button>
    </div>
  );
};

// ── Login Screen ──────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await saFetch('/login', { method: 'POST', body: JSON.stringify(form) });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      if (!res.ok) { setError(data.message || 'Login failed'); return; }
      if (!data.token) { setError('No token received. Try again.'); return; }
      localStorage.setItem('sa_token', data.token);
      localStorage.setItem('sa_admin', JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) {
      console.error('SuperAdmin login error:', err);
      setError('Login failed: ' + (err.message || 'Unknown error'));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#02092D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 400, background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
        <div style={{ background: '#02092D', padding: '28px 32px', textAlign: 'center', borderBottom: '3px solid #FC5C0B' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>CHANAKYA<span style={{ color: '#FC5C0B' }}>X</span></div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4, letterSpacing: 1.5, textTransform: 'uppercase' }}>SuperAdmin Panel</div>
        </div>
        <form onSubmit={handleLogin} style={{ padding: 32 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email Address</label>
            <input type="email" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="superadmin@legalchanakya.in"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Password</label>
            <input type="password" required value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', background: loading ? '#9ca3af' : '#FC5C0B', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Signing in...' : 'Sign In to SuperAdmin'}
          </button>
          <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 16 }}>Protected access — authorized personnel only</p>
        </form>
      </div>
    </div>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = '#02092D' }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px' }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{sub}</div>}
  </div>
);

// ── Main SuperAdmin App ───────────────────────────────────────────────────────
export default function SuperAdmin() {
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sa_admin')); } catch { return null; }
  });
  const [view, setView] = useState('dashboard'); // dashboard | firms | firm-detail | change-password
  const [stats, setStats] = useState(null);
  const [firms, setFirms] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [firmUsers, setFirmUsers] = useState([]);
  const [firmModules, setFirmModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('sa_token');
    if (!token) { setAdmin(null); return; }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await saFetch('/stats');
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const fetchFirms = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      if (filterPlan) params.append('plan', filterPlan);
      if (filterStatus) params.append('status', filterStatus);
      const res = await saFetch(`/firms?${params}`);
      if (res.ok) {
        const data = await res.json();
        setFirms(data.firms); setTotal(data.total); setPages(data.pages);
      }
    } catch {} finally { setLoading(false); }
  }, [page, search, filterPlan, filterStatus]);

  const fetchFirmDetail = async (id) => {
    setLoading(true);
    try {
      const res = await saFetch(`/firms/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedFirm(data.firm);
        setFirmUsers(data.users);
        setFirmModules(data.modules || []);
        setView('firm-detail');
      }
    } catch {} finally { setLoading(false); }
  };

  const fetchPlatformSettings = useCallback(async () => {
    try {
      const res = await saFetch('/settings');
      if (res.ok) setPlatformSettings(await res.json());
    } catch {}
  }, []);

  useEffect(() => { if (admin) { fetchStats(); } }, [admin, fetchStats]);
  useEffect(() => { if (admin && view === 'firms') fetchFirms(); }, [admin, view, fetchFirms]);
  useEffect(() => { if (admin && view === 'settings') fetchPlatformSettings(); }, [admin, view, fetchPlatformSettings]);

  const handlePlanChange = async (firmId, plan, status) => {
    try {
      const res = await saFetch(`/firms/${firmId}/plan`, { method: 'PUT', body: JSON.stringify({ plan, subscription_status: status }) });
      const data = await res.json();
      if (res.ok) { showToast(data.message); setSelectedFirm(data.firm); fetchStats(); }
      else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
  };

  const handleSuspend = async (firmId, is_active) => {
    const action = is_active ? 'activate' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} this firm?`)) return;
    try {
      const res = await saFetch(`/firms/${firmId}/suspend`, { method: 'PUT', body: JSON.stringify({ is_active }) });
      const data = await res.json();
      if (res.ok) { showToast(data.message); setSelectedFirm(data.firm); fetchStats(); }
      else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
  };

  const handleExtendTrial = async (firmId, days) => {
    try {
      const res = await saFetch(`/firms/${firmId}/extend-trial`, { method: 'PUT', body: JSON.stringify({ days }) });
      const data = await res.json();
      if (res.ok) { showToast(data.message); setSelectedFirm(data.firm); }
      else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
  };

  const handleModuleToggle = async (firmId, moduleId, status) => {
    try {
      const res = await saFetch(`/firms/${firmId}/modules/${moduleId}`, { method: 'PUT', body: JSON.stringify({ status }) });
      const data = await res.json();
      if (res.ok) {
        showToast(data.message);
        setFirmModules(prev => prev.map(m => m.module_id === moduleId
          ? { ...m, status: data.subscription.status, expires_at: data.subscription.expires_at }
          : m));
      } else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
  };

  const handleToggleRegistration = async () => {
    const next = !platformSettings?.registration_enabled;
    setSettingsLoading(true);
    try {
      const res = await saFetch('/settings/registration', { method: 'PUT', body: JSON.stringify({ enabled: next }) });
      const data = await res.json();
      if (res.ok) { showToast(data.message); setPlatformSettings(data.settings); }
      else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
    setSettingsLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { showToast('Passwords do not match', 'error'); return; }
    if (pwForm.next.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
    try {
      const res = await saFetch('/change-password', { method: 'PUT', body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }) });
      const data = await res.json();
      if (res.ok) { showToast(data.message); setPwForm({ current: '', next: '', confirm: '' }); }
      else showToast(data.message, 'error');
    } catch { showToast('Server error', 'error'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('sa_token');
    localStorage.removeItem('sa_admin');
    setAdmin(null);
  };

  if (!admin) return <LoginScreen onLogin={(a) => setAdmin(a)} />;

  const T = { bg: '#f5f6fa', white: '#fff', navy: '#02092D', orange: '#FC5C0B', border: '#e5e7eb', text: '#111827', text2: '#6b7280' };

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Sidebar */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: 220, height: '100vh', background: T.navy, display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>CHANAKYA<span style={{ color: T.orange }}>X</span></div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3, letterSpacing: 1.5 }}>SUPERADMIN</div>
        </div>
        <nav style={{ flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '▦' },
            { id: 'firms', label: 'All Firms', icon: '⊞' },
            { id: 'settings', label: 'Platform Settings', icon: '⚙' },
            { id: 'change-password', label: 'Change Password', icon: '🔒' },
          ].map(item => (
            <button key={item.id} onClick={() => setView(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left', background: view === item.id ? 'rgba(252,92,11,0.15)' : 'transparent', color: view === item.id ? T.orange : 'rgba(255,255,255,0.6)', transition: 'all 0.2s' }}>
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{admin.name}</div>
          <button onClick={handleLogout} style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign out →</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ marginLeft: 220, padding: 32 }}>

        {/* ── DASHBOARD ── */}
        {view === 'dashboard' && (
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Dashboard</h1>
            <p style={{ fontSize: 14, color: T.text2, marginBottom: 28 }}>Platform overview — all firms</p>

            {stats ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
                  <StatCard label="Total Firms" value={stats.total_firms} />
                  <StatCard label="Active" value={stats.active_firms} color="#16a34a" />
                  <StatCard label="On Trial" value={stats.trial_firms} color="#d97706" />
                  <StatCard label="Est. MRR" value={fc(stats.mrr)} color={T.orange} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 32 }}>
                  <StatCard label="Expiring in 7 days" value={stats.expiring_soon} color="#d97706" sub="Need attention" />
                  <StatCard label="Expired Trials" value={stats.expired_trials} color="#dc2626" sub="Need follow-up" />
                  <StatCard label="Suspended" value={stats.suspended_firms} color="#6b7280" />
                  <StatCard label="Free Plan" value={stats.free_plan} color="#6b7280" sub={`Starter: ${stats.starter_plan} · Pro: ${stats.pro_plan}`} />
                </div>

                {/* Plan breakdown */}
                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 20 }}>Plan Distribution</h3>
                  <div style={{ display: 'flex', gap: 0, height: 12, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                    {[
                      { label: 'Free', count: parseInt(stats.free_plan), color: '#9ca3af' },
                      { label: 'Starter', count: parseInt(stats.starter_plan), color: '#2563eb' },
                      { label: 'Pro', count: parseInt(stats.pro_plan), color: '#7c3aed' },
                    ].map(p => {
                      const pct = stats.total_firms > 0 ? (p.count / stats.total_firms) * 100 : 0;
                      return pct > 0 ? <div key={p.label} style={{ width: `${pct}%`, background: p.color }} title={`${p.label}: ${p.count}`} /> : null;
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 20 }}>
                    {[
                      { label: 'Free', count: stats.free_plan, color: '#9ca3af' },
                      { label: 'Starter', count: stats.starter_plan, color: '#2563eb' },
                      { label: 'Pro', count: stats.pro_plan, color: '#7c3aed' },
                    ].map(p => (
                      <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
                        <span style={{ fontSize: 12, color: T.text2 }}>{p.label}: <strong style={{ color: T.text }}>{p.count}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>

                <button onClick={() => setView('firms')} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: T.navy, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  View All Firms →
                </button>
              </>
            ) : (
              <div style={{ color: T.text2, fontSize: 14 }}>Loading stats...</div>
            )}
          </div>
        )}

        {/* ── FIRMS LIST ── */}
        {view === 'firms' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 4 }}>All Firms</h1>
                <p style={{ fontSize: 14, color: T.text2 }}>{total} firms registered</p>
              </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search firm name or email..."
                style={{ flex: 1, minWidth: 240, padding: '9px 14px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none' }} />
              <select value={filterPlan} onChange={e => { setFilterPlan(e.target.value); setPage(1); }}
                style={{ padding: '9px 14px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', background: T.white }}>
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
              </select>
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                style={{ padding: '9px 14px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', background: T.white }}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="expired">Expired</option>
              </select>
              <button onClick={fetchFirms} style={{ padding: '9px 18px', background: T.navy, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Search</button>
            </div>

            {/* Table */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.navy }}>
                    {['Firm', 'Plan', 'Status', 'Trial Ends', 'Clients', 'Users', 'Action'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: T.text2 }}>Loading...</td></tr>
                  ) : firms.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: T.text2 }}>No firms found.</td></tr>
                  ) : firms.map((firm, i) => {
                    const dl = daysLeft(firm.trial_ends_at);
                    return (
                      <tr key={firm.id} style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? T.white : '#fafbff' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.white : '#fafbff'}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{firm.name}</div>
                          <div style={{ fontSize: 11, color: T.text2 }}>{firm.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}><PlanBadge plan={firm.plan} /></td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={firm.subscription_status} is_active={firm.is_active} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: dl !== null && dl < 7 ? '#dc2626' : T.text2 }}>
                          {firm.subscription_status === 'trial' ? (dl !== null ? `${dl}d left` : '—') : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2 }}>{firm.client_count}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2 }}>{firm.user_count}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => fetchFirmDetail(firm.id)}
                            style={{ fontSize: 12, fontWeight: 600, color: T.orange, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Manage →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${T.border}`, background: p === page ? T.navy : T.white, color: p === page ? '#fff' : T.text, fontSize: 13, cursor: 'pointer', fontWeight: p === page ? 700 : 400 }}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FIRM DETAIL ── */}
        {view === 'firm-detail' && selectedFirm && (
          <div>
            <button onClick={() => setView('firms')} style={{ fontSize: 13, color: T.orange, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, padding: 0, fontWeight: 500 }}>← Back to Firms</button>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 6 }}>{selectedFirm.name}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <PlanBadge plan={selectedFirm.plan} />
                  <StatusBadge status={selectedFirm.subscription_status} is_active={selectedFirm.is_active} />
                  <span style={{ fontSize: 12, color: T.text2 }}>{selectedFirm.email}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedFirm.is_active
                  ? <button onClick={() => handleSuspend(selectedFirm.id, false)} style={{ padding: '8px 16px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Suspend Firm</button>
                  : <button onClick={() => handleSuspend(selectedFirm.id, true)} style={{ padding: '8px 16px', background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Activate Firm</button>
                }
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Firm info */}
              <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 16 }}>Firm Details</h3>
                {[
                  ['Firm ID', `#${selectedFirm.id}`],
                  ['Firm Code', selectedFirm.firm_code || '—'],
                  ['Phone', selectedFirm.phone || '—'],
                  ['Owner', selectedFirm.owner_name || '—'],
                  ['Registered', fd(selectedFirm.created_at)],
                  ['Trial Ends', selectedFirm.trial_ends_at ? fd(selectedFirm.trial_ends_at) : '—'],
                  ['Clients', selectedFirm.client_count],
                  ['Users', selectedFirm.user_count],
                  ['Leads', selectedFirm.lead_count],
                  ['Invoices', selectedFirm.invoice_count],
                  ['Tasks', selectedFirm.task_count],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
                    <span style={{ color: T.text2 }}>{k}</span>
                    <span style={{ fontWeight: 500, color: T.text }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Manage plan */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 16 }}>Change Plan</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {['free', 'starter', 'pro'].map(plan => (
                      <button key={plan} onClick={() => handlePlanChange(selectedFirm.id, plan, selectedFirm.subscription_status)}
                        style={{ padding: '10px', borderRadius: 8, border: `2px solid ${selectedFirm.plan === plan ? T.navy : T.border}`, background: selectedFirm.plan === plan ? T.navy : T.white, color: selectedFirm.plan === plan ? '#fff' : T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                        {plan}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {['trial', 'active', 'expired'].map(status => (
                      <button key={status} onClick={() => handlePlanChange(selectedFirm.id, selectedFirm.plan, status)}
                        style={{ padding: '8px', borderRadius: 8, border: `2px solid ${selectedFirm.subscription_status === status ? T.orange : T.border}`, background: selectedFirm.subscription_status === status ? T.orange + '15' : T.white, color: selectedFirm.subscription_status === status ? T.orange : T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 16 }}>Extend Trial</h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[7, 14, 30].map(days => (
                      <button key={days} onClick={() => handleExtendTrial(selectedFirm.id, days)}
                        style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        +{days}d
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 16 }}>Module Access</h3>
                  {firmModules.length === 0 && <p style={{ fontSize: 12, color: T.text2 }}>No modules configured yet.</p>}
                  {firmModules.map(m => {
                    const isActive = m.status === 'active' && (!m.expires_at || new Date(m.expires_at) > new Date());
                    const isTrial = m.status === 'trial';
                    return (
                      <div key={m.module_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{m.name}</div>
                          {m.expires_at && <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>Expires {fd(m.expires_at)}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleModuleToggle(selectedFirm.id, m.module_id, 'active')}
                            style={{ padding: '6px 12px', borderRadius: 7, border: `1.5px solid ${isActive ? '#16a34a' : T.border}`, background: isActive ? '#dcfce7' : T.white, color: isActive ? '#16a34a' : T.text2, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            Active
                          </button>
                          <button onClick={() => handleModuleToggle(selectedFirm.id, m.module_id, 'trial')}
                            style={{ padding: '6px 12px', borderRadius: 7, border: `1.5px solid ${isTrial ? T.orange : T.border}`, background: isTrial ? T.orange + '15' : T.white, color: isTrial ? T.orange : T.text2, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            Trial
                          </button>
                          <button onClick={() => handleModuleToggle(selectedFirm.id, m.module_id, 'inactive')}
                            style={{ padding: '6px 12px', borderRadius: 7, border: `1.5px solid ${!isActive && !isTrial ? '#dc2626' : T.border}`, background: !isActive && !isTrial ? '#fee2e2' : T.white, color: !isActive && !isTrial ? '#dc2626' : T.text2, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            Off
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Users table */}
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', marginTop: 20 }}>
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>Users ({firmUsers.length})</h3>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Name', 'Email', 'Role', 'Status', 'Last Login'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.text2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firmUsers.map((u, i) => (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500 }}>{u.full_name}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: T.text2 }}>{u.email}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, textTransform: 'capitalize', color: T.text2 }}>{u.role}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: u.is_active ? '#dcfce7' : '#fee2e2', color: u.is_active ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: T.text2 }}>{u.last_login ? fd(u.last_login) : 'Never'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── PLATFORM SETTINGS ── */}
        {view === 'settings' && (
          <div style={{ maxWidth: 560 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Platform Settings</h1>
            <p style={{ fontSize: 14, color: T.text2, marginBottom: 28 }}>Switches that affect the whole platform, not one firm.</p>

            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: T.navy, marginBottom: 4 }}>New Firm Registration</h3>
                  <p style={{ fontSize: 12, color: T.text2, maxWidth: 380 }}>
                    Controls the "Start Free Trial" signup on the login page. When OFF, nobody can create a new firm —
                    existing firms can still log in normally.
                  </p>
                </div>
                {platformSettings ? (
                  <button
                    onClick={handleToggleRegistration}
                    disabled={settingsLoading}
                    aria-label="Toggle new firm registration"
                    style={{
                      flexShrink: 0, width: 52, height: 30, borderRadius: 20, border: 'none',
                      cursor: settingsLoading ? 'not-allowed' : 'pointer', position: 'relative',
                      background: platformSettings.registration_enabled ? '#16a34a' : '#d1d5db',
                      transition: 'background 0.2s', opacity: settingsLoading ? 0.6 : 1,
                    }}>
                    <span style={{
                      position: 'absolute', top: 3, left: platformSettings.registration_enabled ? 25 : 3,
                      width: 24, height: 24, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                    }} />
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: T.text2 }}>Loading…</div>
                )}
              </div>

              {platformSettings && (
                <div style={{
                  marginTop: 18, padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: platformSettings.registration_enabled ? '#dcfce7' : '#fef3c7',
                  color: platformSettings.registration_enabled ? '#16a34a' : '#92400e',
                }}>
                  {platformSettings.registration_enabled
                    ? '✓ Registration is OPEN — new firms can sign up right now.'
                    : '⏸ Registration is CLOSED — the signup page shows "New signups are paused".'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── CHANGE PASSWORD ── */}
        {view === 'change-password' && (
          <div style={{ maxWidth: 480 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Change Password</h1>
            <p style={{ fontSize: 14, color: T.text2, marginBottom: 28 }}>Update your superadmin account password.</p>
            <form onSubmit={handleChangePassword} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'Current Password', key: 'current' },
                { label: 'New Password', key: 'next' },
                { label: 'Confirm New Password', key: 'confirm' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input type="password" required value={pwForm[key]}
                    onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <button type="submit" style={{ padding: '12px', background: T.navy, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>
                Update Password
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
